import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";

import { AppContexts } from "../../../providers";
import ChecklistItemModal, { ITEM_TYPES } from "../../common/ChecklistItem";
import DragDropSortableList from "../../common/DragDropSortableList";
import SortableChecklistItem from "../../common/SortableChecklistItem";

const CHECKLIST_TYPES = [
  { label: "Cleaning", value: "cleaning" },
  { label: "Inspection", value: "inspection" },
  { label: "Maintenance", value: "maintenance" },
  { label: "Other", value: "other" },
];


const schema = yup.object().shape({
  name: yup.string().required("Checklist name is required").max(255, "Name must be 255 characters or less"),
  type: yup.string().required("Checklist type is required").oneOf(
    CHECKLIST_TYPES.map(t => t.value),
    "Invalid checklist type"
  ),
  description: yup.string().max(500, "Description must be 500 characters or less"),
});

const defaultValues = {
  name: "",
  type: "other", // Use string literal to avoid circular dependency
  description: "",
};

const normalizeItems = (maybe, genUUID) => {
  if (!Array.isArray(maybe)) return [];
  return maybe.map((it) => ({
    id: it.id,
    _uiId: genUUID(), // for React keys
    label: it.label || "",
    item_type: it.item_type || "yes_no",
    config: it.config || null,
    description: it.description || "",
    is_required: it.is_required ?? true,
    require_photo: it.require_photo ?? false,
    require_note: it.require_note ?? false,
  }));
};

// Validate item config based on item_type
const validateItemConfig = (itemType, config) => {
  switch (itemType) {
    case "list":
      if (!config || !Array.isArray(config.items) || config.items.length === 0) {
        return "List items must have at least one option in config.items";
      }
      break;
    case "count":
      if (!config || typeof config.min !== "number" || typeof config.max !== "number") {
        return "Count items must have numeric min and max values";
      }
      if (config.min >= config.max) {
        return "Min must be less than max";
      }
      break;
    case "rating":
      if (!config || typeof config.scale_min !== "number" || typeof config.scale_max !== "number") {
        return "Rating items must have numeric scale_min and scale_max values";
      }
      if (config.scale_min >= config.scale_max) {
        return "Scale min must be less than scale max";
      }
      break;
    case "yes_no":
    case "condition":
      // These don't require config
      break;
    default:
      return "Invalid item type";
  }
  return null;
};

const ChecklistAdd = ({ template }) => {
  const { set, get, settings } = useContext(AppContexts.SettingsContext);
  const { createChecklist, editChecklist, getChecklist, isProcessing } = useContext(AppContexts.ChecklistsContext);
  const { genUUID } = useContext(AppContexts.UtilContext);
  const offCanvasRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const [items, setItems] = useState([]);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  // Task modal state
  const [checkOpen, setCheckOpen] = useState(false);
  const [checkMode, setCheckMode] = useState("add"); // add | edit
  const [checkInitial, setCheckInitial] = useState(null);

  const checklistType = watch("type") ?? defaultValues.type;

  const TypePicker = ({ value, onChange, options }) => (
    <div className="checklist-item-type-btn-group">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`checklist-item-type-btn ${value === opt.value ? "active" : ""}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  // Load template into form + items (and reset when switching templates)
  useEffect(() => {
    if (template) {
      // If template has an ID, fetch full data from backend
      if (template.id) {
        setIsLoadingTemplate(true);
        getChecklist(template.id)
          .then((fullTemplate) => {
            reset({
              name: fullTemplate.name || "",
              type: fullTemplate.type || "other",
              description: fullTemplate.description || "",
            });
            setItems(normalizeItems(fullTemplate.items || [], genUUID));
          })
          .catch((err) => {
            console.error("Error loading checklist:", err);
            // Fallback to template data if fetch fails
            reset({
              name: template.name || "",
              type: template.type || "other",
              description: template.description || "",
            });
            setItems(normalizeItems(template.items || [], genUUID));
          })
          .finally(() => {
            setIsLoadingTemplate(false);
          });
      } else {
        // No ID, just reset form with what we have
        reset({
          name: template.name || "",
          type: template.type || "other",
          description: template.description || "",
        });
        setItems(normalizeItems(template.items || [], genUUID));
      }
    } else {
      reset(defaultValues);
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, reset]);

  useEffect(() => {
    // UIkit sometimes needs a tick to attach events correctly for newly rendered DOM
    const t = setTimeout(() => {
      try {
        UIkit.update?.(offCanvasRef.current);
        UIkit.offcanvas?.(offCanvasRef.current);
      } catch (err) {
        // ignore UIkit issues
      }
    }, 0);

    return () => clearTimeout(t);
  }, []);

  const openAddCheck = () => {
    setCheckMode("add");
    setCheckInitial({
      _uiId: genUUID(),
      label: "",
      item_type: "yes_no",
      config: null,
      is_required: true,
      require_photo: false,
      require_note: false,
      description: "",
    });
    setCheckOpen(true);
  };

  const openEditCheckFromItem = (item) => {
    setCheckMode("edit");
    setCheckInitial({
      id: item.id,
      _uiId: item._uiId,
      label: item.label,
      item_type: item.item_type || "yes_no",
      config: item.config || null,
      is_required: item.is_required ?? true,
      require_photo: item.require_photo ?? false,
      require_note: item.require_note ?? false,
      description: item.description || "",
    });
    setCheckOpen(true);
  };

  const handleUpdateItem = (updated, index) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = updated;
      return copy;
    });
  };

  const handleDeleteItem = (index) => {
    setItems((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      return copy;
    });
  };

  const handleSave = async (formData) => {
    try {
      // Validate all items before submitting
      const itemValidationErrors = [];
      items.forEach((item, idx) => {
        if (!item.label || item.label.trim() === "") {
          itemValidationErrors.push(`Item ${idx + 1}: Label is required`);
        }
        const configError = validateItemConfig(item.item_type, item.config);
        if (configError) {
          itemValidationErrors.push(`Item ${idx + 1} (${item.label || "Unnamed"}): ${configError}`);
        }
      });

      if (itemValidationErrors.length > 0) {
        window.UIkit?.notification(
          `<div>${itemValidationErrors.join("<br/>")}</div>`,
          { status: "danger", timeout: 5000 }
        );
        return;
      }

      // Prepare payload for backend
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description?.trim() || null,
        items: items.map((item) => ({
          // Include id only if exists
          ...(item.id !== undefined ? { id: item.id } : {}),
          label: item.label.trim(),
          item_type: item.item_type,
          config: item.config || null,
          description: item.description?.trim() || null,
          is_required: item.is_required ?? true,
          require_photo: item.require_photo ?? false,
          require_note: item.require_note ?? false,
        })),
      };

      let result;
      if (template?.id) {
        // Update existing checklist
        result = await editChecklist(template.id, payload);
      } else {
        // Create new checklist
        result = await createChecklist(payload);
      }

      window.UIkit?.offcanvas("#checklist-modal")?.hide();
      
    } catch (err) {
      console.error("Error saving checklist:", err);
      const errorMsg = err.response?.data?.error || "Failed to save checklist";
      window.UIkit?.notification(errorMsg, { status: "danger" });
    }
  };

  return (
    <>
      <div
        id="checklist-modal"
        ref={offCanvasRef}
        className="hms-modals uk-offcanvas"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div className="hms-modals-main">
            <button className="uk-offcanvas-close uk-close-large" type="button" uk-close="true" />
            <div className="uk-modal-header">
              <h2 className="uk-modal-title">{template ? "Update" : "Add a"} Checklist</h2>
            </div>

            {isLoadingTemplate ? (
              <div className="uk-modal-body">
                <div className="uk-text-center uk-padding">
                  <div uk-spinner="ratio: 2"></div>
                  <p className="uk-text-meta uk-margin-small-top">Loading checklist...</p>
                </div>
              </div>
            ) : (
              <div className="uk-modal-body uk-flex">
                <form
                  onSubmit={handleSubmit(handleSave)}
                  className="uk-grid-small uk-form-stacked uk-width-1-1"
                  uk-grid="true"
                >
                <div className="uk-width-1-1">
                  <label className="uk-form-label">Name*</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.name })}
                    type="text"
                    {...register("name")}
                  />
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Type*</label>
                  <div className="uk-form-controls">
                    <TypePicker
                      value={checklistType}
                      onChange={(val) =>
                        setValue("type", val, { shouldValidate: true, shouldDirty: true })
                      }
                      options={CHECKLIST_TYPES}
                    />
                    {errors.type && (
                      <div className="uk-text-danger uk-margin-small-top">{errors.type.message}</div>
                    )}
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Description</label>
                  <textarea
                    className={classnames("uk-textarea", { "uk-form-danger": errors.description })}
                    rows="3"
                    {...register("description")}
                  />
                  {errors.description && (
                    <div className="uk-text-danger uk-margin-small-top">{errors.description.message}</div>
                  )}
                </div>

                <hr />

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Checklist Items</label>

                  <DragDropSortableList
                    items={items}
                    onReorder={setItems}
                    itemKeyExtractor={(item) => item._uiId}
                    emptyMessage="No items yet. Click + ITEM to add one."
                    renderItem={(item, index) => (
                      <SortableChecklistItem
                        key={item._uiId}
                        item={item}
                        index={index}
                        onChange={handleUpdateItem}
                        onDelete={handleDeleteItem}
                        onEdit={openEditCheckFromItem}
                        itemTypes={ITEM_TYPES}
                      />
                    )}
                  />

                  <div className="uk-margin-small-top">
                    <button
                      type="button"
                      className="uk-button hms-btn hms-gray-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openAddCheck();
                      }}
                    >
                      + Item
                    </button>
                  </div>
                </div>

                <div className="uk-width-1-1 uk-margin-top">
                  <button 
                    className="uk-button hms-btn hms-red-btn" 
                    type="submit"
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Saving..." : "Save"}
                  </button>
                  <button 
                    className="uk-button hms-btn uk-offcanvas-close" 
                    type="button"
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            )}
          </div>
        </div>
      </div>

      {checkOpen && (
        <ChecklistItemModal
          open={checkOpen}
          mode={checkMode}
          initialValue={checkInitial}
          onClose={() => {
            setCheckOpen(false);
            setCheckInitial(null);
          }}
          onSave={(savedCheck) => {
            console.log("Saving item:", savedCheck);
            
            if (checkMode === "add") {
              // Add new item
              setItems((prev) => [...prev, savedCheck]);
            } else {
              // Update existing item by _uiId
              setItems((prev) =>
                prev.map((item) => (item._uiId === savedCheck._uiId ? savedCheck : item))
              );
            }
            
            // Modal will close itself via onClose
          }}
        />
      )}
    </>
  );
};

export default ChecklistAdd;
